import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  AutoAwesome as MagicIcon,
  CloudUpload as CloudUploadIcon,
  ListAlt as ReviewIcon,
  InfoOutlined as InfoIcon,
  DeleteOutline as TrashIcon,
} from '@mui/icons-material';
import GetRatesContext from '../../../../context/GetRates';
import { GetRatesContextType } from '../../Type';
import { extractShipmentFromText, extractShipmentFromInvoice } from '../../services/aiService';
import SavedLocations from './SavedLocations';
import { validatePincode, checkWeightConsistency, AISavedLocation } from '../../services/aiService';

interface Design2WorkspaceProps {
  aiFilledFields: string[];
  setAiFilledFields: (fields: string[]) => void;
  isBoxDescBlocking: boolean;
  onValidationChange: (hasError: boolean) => void;
}

export default function Design2Workspace({
  aiFilledFields,
  setAiFilledFields,
  isBoxDescBlocking,
  onValidationChange,
}: Design2WorkspaceProps) {
  const {
    hideBoxes,
    setHideBoxes,
    boxes,
    setBoxes,
    ratesInput,
    setRateInput,
    pickup,
    setPickup,
    destination,
    setDestination,
  } = React.useContext(GetRatesContext) as GetRatesContextType;

  // Active workspace state: input mode and raw content
  const [inputText, setInputText] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);

  // Validation Warnings
  const [pickupPincodeSuggestion, setPickupPincodeSuggestion] = useState<string | null>(null);
  const [destPincodeSuggestion, setDestPincodeSuggestion] = useState<string | null>(null);
  const [weightWarning, setWeightWarning] = useState<string | null>(null);
  const [isWeightConfirmed, setIsWeightConfirmed] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Suggestions/Templates for natural language
  const PROMPT_SUGGESTIONS = [
    '2 boxes from Delhi to Mumbai, 15 kg total, electronics, 40x30x20 cm',
    'Document from Bangalore to Chennai, weight 0.5 kg',
    'Heavy cargo from Kolkata to London, weight 120 kg, 3 items',
  ];

  // Sync validation errors to block/allow Get Rates
  const missingPickup = !pickup.city || !ratesInput.pickupPinCode;
  const missingDest = !destination.city || !ratesInput.destinationPinCode;
  const missingWeight = !ratesInput.weight || Number(ratesInput.weight) <= 0;
  const missingBoxDescriptions =
    ratesInput.shipmentType === 'parcel' && !hideBoxes && boxes.some((b: any) => !b.description?.trim());

  const hasBlockingErrors =
    missingPickup || missingDest || missingWeight || (isBoxDescBlocking && missingBoxDescriptions);

  useEffect(() => {
    onValidationChange(hasBlockingErrors);
  }, [hasBlockingErrors, onValidationChange]);

  // Trigger Pincode Suggestion validations
  useEffect(() => {
    if (pickup.city && ratesInput.pickupPinCode) {
      const check = validatePincode(ratesInput.pickupPinCode, pickup.city);
      setPickupPincodeSuggestion(check.valid ? null : check.suggestion || null);
    } else {
      setPickupPincodeSuggestion(null);
    }
  }, [pickup.city, ratesInput.pickupPinCode]);

  useEffect(() => {
    if (destination.city && ratesInput.destinationPinCode) {
      const check = validatePincode(ratesInput.destinationPinCode, destination.city);
      setDestPincodeSuggestion(check.valid ? null : check.suggestion || null);
    } else {
      setDestPincodeSuggestion(null);
    }
  }, [destination.city, ratesInput.destinationPinCode]);

  // Trigger Weight Consistency check
  useEffect(() => {
    if (ratesInput.weight && !isWeightConfirmed) {
      const descText = ratesInput.shipmentType === 'document' ? 'document' : boxes[0]?.description || 'General Goods';
      const check = checkWeightConsistency(
        Number(ratesInput.weight),
        ratesInput.shipmentType === 'document' ? 1 : boxes.length,
        descText,
      );
      setWeightWarning(check.status === 'warning' ? check.message || null : null);
    } else {
      setWeightWarning(null);
    }
  }, [ratesInput.weight, boxes, ratesInput.shipmentType, isWeightConfirmed]);

  const handleSavedLocationSelect = (loc: AISavedLocation, target: 'pickup' | 'destination') => {
    const formatted = {
      city: loc.city,
      state: loc.state,
      country: loc.country,
      pincode: loc.pincode,
    };
    if (target === 'pickup') {
      setPickup(formatted);
      setRateInput({ ...ratesInput, pickup: formatted, pickupPinCode: loc.pincode });
    } else {
      setDestination(formatted);
      setRateInput({ ...ratesInput, destination: formatted, destinationPinCode: loc.pincode });
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setAttachedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachedFile(e.target.files[0]);
    }
  };

  const executeExtraction = async () => {
    setIsProcessing(true);
    setProcessingProgress(25);

    const interval = setInterval(() => {
      setProcessingProgress(prev => Math.min(prev + 20, 95));
    }, 150);

    try {
      let result: any = null;
      if (attachedFile) {
        result = await extractShipmentFromInvoice(attachedFile.name);
      } else if (inputText.trim()) {
        result = extractShipmentFromText(inputText);
      }

      clearInterval(interval);
      setProcessingProgress(100);

      setTimeout(() => {
        if (result) {
          setPickup(result.pickup);
          setDestination(result.destination);
          setHideBoxes(result.hideBoxes);
          if (result.boxes) {
            setBoxes(result.boxes);
          }
          setRateInput({
            shipmentType: result.shipmentType,
            pickup: result.pickup,
            destination: result.destination,
            pickupPinCode: result.pickupPinCode,
            destinationPinCode: result.destinationPinCode,
            weight: result.weight,
            weightUnit: result.weightUnit,
          });

          setAiFilledFields([
            'pickup',
            'destination',
            'pickupPinCode',
            'destinationPinCode',
            'shipmentType',
            'weight',
            'boxes',
          ]);
        }
        setIsProcessing(false);
        setInputText('');
        setAttachedFile(null);
      }, 350);
    } catch (error) {
      console.error(error);
      clearInterval(interval);
      setIsProcessing(false);
    }
  };

  const handleBoxDescChange = (index: number, val: string) => {
    const updated = [...boxes];
    updated[index] = { ...updated[index], description: val };
    setBoxes(updated);
  };

  const isFieldAiFilled = (fieldName: string) => aiFilledFields.includes(fieldName);

  return (
    <Grid container spacing={3} sx={{ p: 1 }}>
      {/* LEFT: AI Workspace Control Panel */}
      <Grid item xs={12} md={5}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: '16px',
            height: '100%',
            borderColor: 'primary.light',
            boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
          }}
        >
          <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MagicIcon sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle1" fontWeight="bold">
                AI Command Center
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
              Control your rate calculation context here. Select saved locations, upload invoices, or write shipment
              commands.
            </Typography>

            {/* Step 1: Saved Location Shortcuts */}
            <SavedLocations type="shipper" onSelect={loc => handleSavedLocationSelect(loc, 'pickup')} />
            <SavedLocations type="consignee" onSelect={loc => handleSavedLocationSelect(loc, 'destination')} />

            {/* Step 2: Natural text command input */}
            <Box sx={{ mb: 2, borderTop: '1px solid #EAEAEA', pt: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="600"
                sx={{ display: 'block', mb: 1, textTransform: 'uppercase' }}
              >
                Option A: Natural Language
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                placeholder="Describe details: '3 boxes to London from Delhi, 45 kg...'"
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                disabled={isProcessing || !!attachedFile}
                sx={{ backgroundColor: '#FAFAFA', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />

              {/* Shortcut Prompts */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {PROMPT_SUGGESTIONS.map((s, idx) => (
                  <Chip
                    key={idx}
                    label={`Try "${s.substring(0, 15)}..."`}
                    size="small"
                    onClick={() => setInputText(s)}
                    disabled={isProcessing || !!attachedFile}
                    sx={{ fontSize: '10px', height: '20px', cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>

            {/* Step 3: Invoice Upload Area */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="600"
                sx={{ display: 'block', mb: 1, textTransform: 'uppercase' }}
              >
                Option B: Invoice Attachment
              </Typography>

              {attachedFile ? (
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid #E4E4E4',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: '#F8F9FB',
                  }}
                >
                  <CloudUploadIcon sx={{ color: 'primary.main' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      flexGrow: 1,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      fontWeight: '500',
                    }}
                  >
                    {attachedFile.name}
                  </Typography>
                  <IconButton onClick={() => setAttachedFile(null)} size="small" color="error">
                    <TrashIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  onDragOver={e => e.preventDefault()}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: '2px dashed #D0D0D0',
                    borderRadius: '12px',
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#FAFAFA',
                    '&:hover': { borderColor: 'primary.main', backgroundColor: '#F4F8FD' },
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    accept=".pdf,.png,.jpg,.jpeg"
                  />
                  <CloudUploadIcon sx={{ fontSize: 24, color: 'text.secondary', mb: 0.5 }} />
                  <Typography variant="caption" display="block" fontWeight="500">
                    Click or drag invoice file here
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Process Action */}
            <Button
              variant="contained"
              fullWidth
              onClick={executeExtraction}
              disabled={isProcessing || (!inputText.trim() && !attachedFile)}
              startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <MagicIcon />}
              sx={{ textTransform: 'none', borderRadius: '24px', py: 1.2, fontWeight: 'bold' }}
            >
              {isProcessing ? `Extracting... (${processingProgress}%)` : 'Run AI Extraction'}
            </Button>
          </CardContent>
        </Card>
      </Grid>

      {/* RIGHT: Structured Workspace Review Panel */}
      <Grid item xs={12} md={7}>
        <Card
          variant="outlined"
          sx={{ borderRadius: '16px', borderColor: '#EAEAEA', boxShadow: '0 4px 20px rgba(0,0,0,0.01)' }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, borderBottom: '1px solid #EAEAEA', pb: 1.5 }}
            >
              <ReviewIcon sx={{ color: 'success.main' }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Shipment Review & Validation
              </Typography>
            </Box>

            {/* Extraction highlight banner */}
            {aiFilledFields.length > 0 && (
              <Alert severity="success" sx={{ mb: 3, borderRadius: '12px', py: 0.5 }}>
                AI extracted shipment specs. Please review the highlighted fields below.
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* Route Summary */}
              <Grid item xs={12} sm={6}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: missingPickup ? '1px solid #FFCDD2' : '1px dashed #DDD',
                    backgroundColor: isFieldAiFilled('pickup') ? '#F4F8FD' : 'transparent',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    SHIPPER / PICKUP
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>
                    {pickup.city ? `${pickup.city}, ${pickup.state}, ${pickup.country}` : 'Not entered'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pincode:{' '}
                  </Typography>
                  <Typography variant="caption" fontWeight="500" color="text.primary">
                    {ratesInput.pickupPinCode || 'None'}
                  </Typography>
                  {pickupPincodeSuggestion && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={`Fix pin to: ${pickupPincodeSuggestion}`}
                        color="warning"
                        size="small"
                        onClick={() => setRateInput({ ...ratesInput, pickupPinCode: pickupPincodeSuggestion })}
                        sx={{ fontSize: '10px', height: '20px' }}
                      />
                    </Box>
                  )}
                  {missingPickup && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                      Required for rate calculation
                    </Typography>
                  )}
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: missingDest ? '1px solid #FFCDD2' : '1px dashed #DDD',
                    backgroundColor: isFieldAiFilled('destination') ? '#F4F8FD' : 'transparent',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    CONSIGNEE / DESTINATION
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>
                    {destination.city
                      ? `${destination.city}, ${destination.state}, ${destination.country}`
                      : 'Not entered'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pincode:{' '}
                  </Typography>
                  <Typography variant="caption" fontWeight="500" color="text.primary">
                    {ratesInput.destinationPinCode || 'None'}
                  </Typography>
                  {destPincodeSuggestion && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={`Fix pin to: ${destPincodeSuggestion}`}
                        color="warning"
                        size="small"
                        onClick={() => setRateInput({ ...ratesInput, destinationPinCode: destPincodeSuggestion })}
                        sx={{ fontSize: '10px', height: '20px' }}
                      />
                    </Box>
                  )}
                  {missingDest && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                      Required for rate calculation
                    </Typography>
                  )}
                </Card>
              </Grid>

              {/* Weight & Type Summary */}
              <Grid item xs={12} sm={6}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    backgroundColor: isFieldAiFilled('shipmentType') ? '#F4F8FD' : 'transparent',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    SHIPMENT TYPE
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'uppercase', mt: 0.5 }}>
                    {ratesInput.shipmentType}
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: missingWeight ? '1px solid #FFCDD2' : '1px dashed #DDD',
                    backgroundColor: isFieldAiFilled('weight') ? '#F4F8FD' : 'transparent',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight="500">
                    TOTAL WEIGHT
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>
                    {ratesInput.weight} {ratesInput.weightUnit}
                  </Typography>
                  {weightWarning && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                      <Chip
                        label="Verify Weight"
                        color="info"
                        size="small"
                        onClick={() => setIsWeightConfirmed(true)}
                        sx={{ fontSize: '10px', height: '20px' }}
                      />
                    </Box>
                  )}
                  {missingWeight && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                      Required for rate calculation
                    </Typography>
                  )}
                </Card>
              </Grid>

              {/* Box descriptions review */}
              {ratesInput.shipmentType === 'parcel' && !hideBoxes && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="600"
                      sx={{ display: 'block', mb: 1.5 }}
                    >
                      BOX LIST DESCRIPTIONS ({boxes.length} BOXES)
                    </Typography>
                    {boxes.map((box: any, idx: number) => {
                      const descMissing = !box.description?.trim();
                      return (
                        <Box
                          key={box.id}
                          sx={{
                            p: 1.5,
                            border: '1px solid #EAEAEA',
                            borderColor: descMissing ? '#FF8A80' : '#EAEAEA',
                            borderRadius: '8px',
                            mb: 1,
                            backgroundColor: descMissing ? '#FFEFEF' : '#FFF',
                          }}
                        >
                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={3}>
                              <Typography variant="caption" fontWeight="bold">
                                Box {idx + 1}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {box.weight}kg, {box.length}x{box.width}x{box.height}cm
                              </Typography>
                            </Grid>
                            <Grid item xs={9}>
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Commodity (e.g. Spare Parts, Electronics)"
                                value={box.description || ''}
                                onChange={e => handleBoxDescChange(idx, e.target.value)}
                                sx={{
                                  '& .MuiOutlinedInput-root': { borderRadius: '6px', height: '32px', fontSize: '12px' },
                                }}
                              />
                              {descMissing && (
                                <Typography variant="caption" color="error" sx={{ fontSize: '9px', fontWeight: '500' }}>
                                  {isBoxDescBlocking
                                    ? '[BLOCKING] Product description needed'
                                    : '[ADVISORY] Description missing'}
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      );
                    })}
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Quick Helper Tips */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: '#FFFDF6',
                borderRadius: '12px',
                border: '1px solid #FFE0B2',
                display: 'flex',
                gap: 1,
              }}
            >
              <InfoIcon color="warning" sx={{ fontSize: 18 }} />
              <Box>
                <Typography variant="caption" fontWeight="bold" color="warning.dark">
                  Workspace Note
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  If there are any validation warnings (such as pincode mismatch), you can resolve them by clicking the
                  quick-action chips above. To perform detailed editing, you can switch back to the "AI-Enhanced Form"
                  design at the top of the tab.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
